import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { mountDevTools } from '@src/store/utils/mount-dev-tools';

type ClientInformationState = {
	newClient: boolean | null;
	firstName: string;
	lastName: string;
	phoneNumber: string;
	email: string;
	addressLine1: string;
	addressLine2?: string;
	city: string;
	state: string;
	zipCode: string;
	clientId: string;
	coowners?: {
		id: string;
		name: string;
		notes?: string;
		relationship?: string;
		relationshipId?: string;
		phoneNumber?: string;
		email?: string;
		associatedPets?: {
			id: string;
			name: string;
		}[];
	}[];
	smsMarketing: boolean;
	smsTransactional: boolean;
	emailMarketing: boolean;
	emailTransactional: boolean;
	isInCollections?: boolean;
};

type ClientInformationActions = {
	setNewClient: (newClient: boolean) => void;
	setFirstName: (firstName: string) => void;
	setLastName: (lastName: string) => void;
	setPhoneNumber: (phoneNumber: string) => void;
	setEmail: (email: string) => void;
	setAddressLine1: (addressLine1: string) => void;
	setAddressLine2: (addressLine2?: string) => void;
	setCity: (city: string) => void;
	setState: (state: string) => void;
	setZipCode: (zipCode: string) => void;
	setClientId: (clientId: string) => void;
	setAllClientInformation: (clientInfo: ClientInformationState) => void;
	setCoowners: (coowners: ClientInformationState['coowners']) => void;
	updateCoowner: (coownerId: string, updatedInfo: Partial<ClientInformationState['coowners']>) => void;
	deleteCoowner: (coownerId: string) => void;
	reset: () => void;
	setSmsMarketing: (smsMarketing: boolean) => void;
	setSmsTransactional: (smsTransactional: boolean) => void;
	setEmailMarketing: (emailMarketing: boolean) => void;
	setEmailTransactional: (emailTransactional: boolean) => void;
};

const INITIAL_STATE: ClientInformationState = {
	newClient: null,
	firstName: '',
	lastName: '',
	phoneNumber: '',
	email: '',
	addressLine1: '',
	addressLine2: '',
	city: '',
	state: '',
	zipCode: '',
	clientId: '',
	coowners: [],
	smsMarketing: true,
	smsTransactional: true,
	emailMarketing: true,
	emailTransactional: true,
	isInCollections: false,
};

const useClientInformationStore = create<ClientInformationState & ClientInformationActions>()(
	persist(
		(set) => ({
			...INITIAL_STATE,
			setNewClient: (newClient) => set({ newClient }),
			setFirstName: (firstName) => set({ firstName }),
			setLastName: (lastName) => set({ lastName }),
			setPhoneNumber: (phoneNumber) => set({ phoneNumber }),
			setEmail: (email) => set({ email }),
			setAddressLine1: (addressLine1) => set({ addressLine1 }),
			setAddressLine2: (addressLine2) => set({ addressLine2 }),
			setCity: (city) => set({ city }),
			setState: (state) => set({ state }),
			setZipCode: (zipCode) => set({ zipCode }),
			setClientId: (clientId) => set({ clientId }),
			setAllClientInformation: (clientInfo) => set((prevState) => ({ ...prevState, ...clientInfo })),
			setCoowners: (coowners) => set({ coowners }),
			updateCoowner: (coownerId, updatedInfo) =>
				set((state) => ({
					coowners: state.coowners?.map((coowner) =>
						coowner.id === coownerId ? { ...coowner, ...updatedInfo } : coowner
					),
				})),
			deleteCoowner: (coownerId) =>
				set((state) => ({
					coowners: state.coowners?.filter((coowner) => coowner.id !== coownerId),
				})),
			reset: () => set(INITIAL_STATE),
			setSmsMarketing: (smsMarketing) => set({ smsMarketing }),
			setSmsTransactional: (smsTransactional) => set({ smsTransactional }),
			setEmailMarketing: (emailMarketing) => set({ emailMarketing }),
			setEmailTransactional: (emailTransactional) => set({ emailTransactional }),
		}),
		{ name: 'book-an-appointment-client-information' }
	)
);

mountDevTools('useClientInformationStore', useClientInformationStore);

export default useClientInformationStore;
